import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

// Async thunk to register as vendor
export const registerVendor = createAsyncThunk(
  "vendor/register",
  async (vendorData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/api/vendors/register`,
        vendorData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to register as vendor" }
      );
    }
  }
);

// Async thunk to get vendor profile
export const getVendorProfile = createAsyncThunk(
  "vendor/getProfile",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL}/api/vendors/profile`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch vendor profile" }
      );
    }
  }
);

// Async thunk to update vendor profile
export const updateVendorProfile = createAsyncThunk(
  "vendor/updateProfile",
  async (updateData, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${import.meta.env.VITE_BACKEND_URL}/api/vendors/profile`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to update vendor profile" }
      );
    }
  }
);

// Async thunk to get dashboard stats
export const getVendorStats = createAsyncThunk(
  "vendor/getStats",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL}/api/vendors/dashboard/stats`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch vendor stats" }
      );
    }
  }
);

// Async thunk to check application status
export const checkApplicationStatus = createAsyncThunk(
  "vendor/checkStatus",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL}/api/vendors/application-status`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to check application status" }
      );
    }
  }
);

// Async thunk to get all vendors (admin)
export const getAllVendors = createAsyncThunk(
  "vendor/getAllVendors",
  async ({ status, search, sortBy }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (status) params.append("status", status);
      if (search) params.append("search", search);
      if (sortBy) params.append("sortBy", sortBy);

      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL}/api/admin/vendors?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch vendors" }
      );
    }
  }
);

// Async thunk to approve vendor (admin)
export const approveVendor = createAsyncThunk(
  "vendor/approve",
  async (vendorId, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${import.meta.env.VITE_BACKEND_URL}/api/admin/vendors/${vendorId}/approve`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to approve vendor" }
      );
    }
  }
);

// Async thunk to reject vendor (admin)
export const rejectVendor = createAsyncThunk(
  "vendor/reject",
  async ({ vendorId, reason }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${import.meta.env.VITE_BACKEND_URL}/api/admin/vendors/${vendorId}/reject`,
        { reason },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to reject vendor" }
      );
    }
  }
);

const vendorSlice = createSlice({
  name: "vendor",
  initialState: {
    profile: null,
    stats: null,
    applicationStatus: null,
    vendors: [], // For admin
    loading: false,
    error: null,
    updateLoading: false,
  },
  reducers: {
    clearVendorError: (state) => {
      state.error = null;
    },
    clearVendorData: (state) => {
      state.profile = null;
      state.stats = null;
      state.applicationStatus = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Register Vendor
      .addCase(registerVendor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerVendor.fulfilled, (state, action) => {
        state.loading = false;
        state.applicationStatus = "pending";
        state.error = null;
      })
      .addCase(registerVendor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to register";
      })

      // Get Vendor Profile
      .addCase(getVendorProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getVendorProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload;
        state.error = null;
      })
      .addCase(getVendorProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch profile";
      })

      // Update Vendor Profile
      .addCase(updateVendorProfile.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(updateVendorProfile.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.profile = action.payload.vendor;
        state.error = null;
      })
      .addCase(updateVendorProfile.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload?.message || "Failed to update profile";
      })

      // Get Vendor Stats
      .addCase(getVendorStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getVendorStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload.stats;
        state.error = null;
      })
      .addCase(getVendorStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch stats";
      })

      // Check Application Status
      .addCase(checkApplicationStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkApplicationStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.applicationStatus = action.payload.applicationStatus;
        state.profile = action.payload.vendor;
        state.error = null;
      })
      .addCase(checkApplicationStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to check status";
      })

      // Get All Vendors (Admin)
      .addCase(getAllVendors.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllVendors.fulfilled, (state, action) => {
        state.loading = false;
        state.vendors = action.payload.vendors;
        state.error = null;
      })
      .addCase(getAllVendors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch vendors";
      })

      // Approve Vendor
      .addCase(approveVendor.fulfilled, (state, action) => {
        const index = state.vendors.findIndex(
          (v) => v._id === action.payload.vendor._id
        );
        if (index !== -1) {
          state.vendors[index] = action.payload.vendor;
        }
      })

      // Reject Vendor
      .addCase(rejectVendor.fulfilled, (state, action) => {
        const index = state.vendors.findIndex(
          (v) => v._id === action.payload.vendor._id
        );
        if (index !== -1) {
          state.vendors[index] = action.payload.vendor;
        }
      });
  },
});

export const { clearVendorError, clearVendorData } = vendorSlice.actions;
export default vendorSlice.reducer;
